import { useDocumentTitle as useMantineDocumentTitle } from '@mantine/hooks';
import { useUserClientContext } from '@/app/clientContext';
import { PAGE_TITLE_SUFFIX } from '@/app/constants';
import { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import routes from '@/app/routes';
import { useDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import { captureException } from '@sentry/core';

interface DocumentTitleOptions {
  title?: string;
  paths: string[]; // Always an array of paths
}

/**
 * Custom hook to update the document title with client context.
 * Uses Mantine's `useDocumentTitle` under the hood.
 *
 * Can be used in two ways:
 * 1. `useDocumentTitle()` - automatically determines title from the `name` key in `routes.ts`
 * 2. `useDocumentTitle("Custom Title", ["/route/path"])` - registers dynamic title for specific route
 *   - If multiple paths are provided, title applies to all listed routes
 *   - Use `'*'` in paths to apply title globally when mounted
 */
export const useDocumentTitle = (options?: DocumentTitleOptions) => {
  const auth = useUserClientContext();
  const location = useLocation();
  const deliveryIdentifier = useDeliveryIdentifier();

  const fullTitle = useMemo(() => {
    const clientName = auth?.context?.name;

    let pageTitle: string | undefined;

    if (options?.title && options?.paths) {
      if (options.paths.includes('*')) {
        // Apply title globally if '*' is in paths
        pageTitle = options.title;
      } else {
        // Check if current route matches any of the provided paths
        const match = options.paths.some((path) =>
          matchPath({ path, end: true }, location.pathname)
        );

        if (match) {
          pageTitle = options.title;
        } else {
          // Don't set any title if we're not on the matching route
          return undefined;
        }
      }
    } else if (options?.paths) {
      // A caller with a title that has not arrived yet. Leave the title alone.
      return undefined;
    } else if (!options) {
      // This is the default - get title from routes.ts
      pageTitle =
        routes
          .map((route) => {
            const match = matchPath(
              { path: route.path, end: true },
              location.pathname
            );
            return match ? route.name : null;
          })
          .find(Boolean) || undefined;
    } else {
      // Invalid usage
      captureException(
        'useDocumentTitle called with invalid parameters. Pass either no options or an object with title and paths.'
      );
      return undefined;
    }

    if (!clientName || !pageTitle || clientName === 'Loading...') {
      return pageTitle ?? 'Overstory';
    }

    if (deliveryIdentifier) {
      return `Delivery ${deliveryIdentifier} - ${clientName}${PAGE_TITLE_SUFFIX}`;
    } else {
      return `${pageTitle} - ${clientName}${PAGE_TITLE_SUFFIX}`;
    }
  }, [auth, location.pathname, options, deliveryIdentifier]);

  // Mantine ignores a blank title, so an empty string leaves the document
  // title as it is.
  useMantineDocumentTitle(fullTitle ?? '');
};
